import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiJson } from "../utils/fetch";
import type {
  Invitation,
  Org,
  OrgMember,
  Project,
  UserProject,
} from "../types";

export function useUserProjects(userId: string) {
  return useQuery({
    queryKey: ["users", userId, "projects"],
    queryFn: () => apiJson<UserProject[]>(`/users/${userId}/projects`),
    enabled: Boolean(userId),
    refetchOnWindowFocus: false,
  });
}

export function useCreateUserProject(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      apiJson<UserProject>(`/users/${userId}/projects`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId, "projects"] });
    },
  });
}

export function useDeleteUserProject(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      apiJson<{ ok: boolean }>(`/users/${userId}/projects/${projectId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId, "projects"] });
    },
  });
}

export function useOrgs() {
  return useQuery({
    queryKey: ["orgs"],
    queryFn: () => apiJson<Org[]>("/orgs"),
    refetchOnWindowFocus: false,
  });
}

export function useProjects(orgId: string) {
  return useQuery({
    queryKey: ["orgs", orgId, "projects"],
    queryFn: () => apiJson<Project[]>(`/orgs/${orgId}/projects`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
  });
}

export function useCreateProject(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      apiJson<Project>(`/orgs/${orgId}/projects`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "projects"] });
    },
  });
}

export function useUpdateOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiJson<Org>(`/orgs/${orgId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useRequestOrgDeletion(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ pending_deletion_at: string }>(`/orgs/${orgId}/delete`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
    },
  });
}

export function useOrgMembers(orgId: string) {
  return useQuery({
    queryKey: ["orgs", orgId, "members"],
    queryFn: () => apiJson<OrgMember[]>(`/orgs/${orgId}/members`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiJson<{ ok: boolean }>(`/orgs/${orgId}/members/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "members"] });
    },
  });
}

export function useInviteMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { invitee_email: string; role: string }) =>
      apiJson<{ id: string }>(`/orgs/${orgId}/invitations`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "invitations"] });
    },
  });
}

export function useTransferOwnership(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (newOwnerId: string) =>
      apiJson<{ ok: boolean }>(`/orgs/${orgId}/transfer-ownership`, {
        method: "POST",
        body: JSON.stringify({ new_owner_id: newOwnerId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
      qc.invalidateQueries({ queryKey: ["user"] });
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "members"] });
    },
  });
}

export function useRevokeInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiJson<{ ok: boolean }>(`/orgs/${orgId}/invitations/${invitationId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", orgId, "invitations"] });
    },
  });
}

export function useOrgInvitations(orgId: string) {
  return useQuery({
    queryKey: ["orgs", orgId, "invitations"],
    queryFn: () => apiJson<Invitation[]>(`/orgs/${orgId}/invitations`),
    enabled: Boolean(orgId),
    refetchOnWindowFocus: false,
  });
}

export function useCancelOrgDeletion(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{ ok: boolean }>(`/orgs/${orgId}/delete/cancel`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
    },
  });
}
